import type { RetryableConnection, RetryableTable, RunScopeSlice } from "@shared/types/runs";
import { FALLBACK_ZONE, calendarDayInZone, formatInstant, zoneLabel } from "@/lib/time/displayTime";

/**
 * Words for table-level runs (product gaps row 2): retry only the tables that did not land, a Run
 * now of some tables, a re-sync of one table. Pure, so every sentence is tested without a screen.
 */

/** "1 table", "3 tables". */
export function tablesWord(count: number): string {
  return `${count.toLocaleString("en-US")} ${count === 1 ? "table" : "tables"}`;
}

/** Tables that did not land, over every connection of the run. */
export function retryableCount(retryable: readonly RetryableConnection[] | undefined): number {
  return (retryable ?? []).reduce((sum, connection) => sum + connection.tables.length, 0);
}

/**
 * "Retry 3 tables" for the callout, or null when nothing is left to retry. The dialog retries one
 * connection at a time, so with more than one connection to retry the label names no count: a
 * sum over connections would promise more than one press retries.
 */
export function retryTablesLabel(
  retryable: readonly RetryableConnection[] | undefined,
): string | null {
  const count = retryableCount(retryable);
  if (count === 0) return null;
  const connections = (retryable ?? []).filter((connection) => connection.tables.length > 0);
  return connections.length > 1 ? "Retry tables" : `Retry ${tablesWord(count)}`;
}

/**
 * Product gaps row 4: a cause's retry button, "Retry the 3 failed tables", or null when nothing
 * is left to retry. The same count rule as `retryTablesLabel`: no count across connections.
 */
export function retryFailedTablesLabel(
  retryable: readonly RetryableConnection[] | undefined,
): string | null {
  const count = retryableCount(retryable);
  if (count === 0) return null;
  const connections = (retryable ?? []).filter((connection) => connection.tables.length > 0);
  if (connections.length > 1) return "Retry the failed tables";
  return count === 1
    ? "Retry the failed table"
    : `Retry the ${count.toLocaleString("en-US")} failed tables`;
}

/** The tables a scoped run covered, over its connections; null for a run that covered them all. */
export function scopeTableCount(scope: readonly RunScopeSlice[] | undefined): number | null {
  if (!scope || scope.length === 0) return null;
  return scope.reduce((sum, slice) => sum + slice.tableCount, 0);
}

/** "Retry of 7c3a9e01": the source run, by its first eight characters. */
export function retryOfLabel(runId: string): string {
  return `Retry of ${runId.slice(0, 8)}`;
}

/**
 * The skew of retrying some tables up to now (owner answer of 2026-09-28: stated on screen,
 * design section 3). Said before the button is pressed.
 */
export const SKEW_SENTENCE =
  "The other tables of this connection keep what this run read. A table extracted again is read up to now, so it may hold rows that refer to records the other tables pick up on their next import. A table loaded from its staged files holds what this run read, and is offered only while no later import has landed it. Nothing is lost: every table reads from its own last successful import.";

/** The title over `SKEW_SENTENCE`. */
export const SKEW_TITLE = "Retried tables can be newer than the rest";

/** What happened to a table in the run, as its row in the retry picker says it. */
export function whatHappened(table: Pick<RetryableTable, "failedStep" | "outcome"> | null): string {
  if (!table) return "Landed";
  if (table.failedStep === "load") {
    // Review r2, B2: a load retry leaves alone a table a later import has landed since.
    return table.outcome === "skipped" ? "Load skipped: a later import landed it" : "Load failed";
  }
  return table.outcome === "skipped" ? "Skipped by the extract" : "Extract failed";
}

/**
 * How the retry reads a table again: from the files this run staged, or extracted with the mode
 * it keeps from the run (a re-sync whole, a backfill from its date). A table the reader adds
 * (null) is read from its own watermark.
 *
 * #1077: `readsEveryRow` is a connection whose connector cannot import incrementally (SQL Server,
 * Paylocity). Its tables have no watermark, so the extract reads every row whatever the mode
 * (`runner/config/bundle.ts` `bundleEntity`), and only staged files are anything else.
 */
export function howReadAgain(
  table: Pick<RetryableTable, "retryFrom" | "mode" | "since"> | null,
  readsEveryRow = false,
  zone: string = FALLBACK_ZONE,
): string {
  if (table?.retryFrom === "staged") return "Loads the staged files again";
  if (readsEveryRow || table?.mode === "full") return "Read again in full";
  // #1078: the stored start is an instant, read in the reader's display zone, named; slicing its
  // UTC text would show the day before for a reader east of UTC whose midnight it is. The year
  // stays (#1078 review): a backfill can start in any year from 1900.
  if (table?.mode === "since" && table.since) {
    return `Read again from ${formatInstant(table.since, zone, "minutes", { named: true })}`;
  }
  return "Extracted again from its own watermark";
}

/** The confirm sentence of a re-sync of one table (design section 4.5, point 4). */
export function resyncSentence(table: string, destination: string): string {
  return `Deletes and reloads every row of ${table} in ${destination}. Other tables are not touched.`;
}

/**
 * The reader's own date as `YYYY-MM-DD`, the latest a since date may be (review r1, N2): today in
 * their display zone (#1078), the zone whose midnight the date starts at.
 */
export function localDateIso(now: Date = new Date(), zone: string = FALLBACK_ZONE): string {
  const today = calendarDayInZone(now, zone);
  if (!today) return now.toISOString().slice(0, 10);
  const month = String(today.month).padStart(2, "0");
  const day = String(today.day).padStart(2, "0");
  return `${today.year}-${month}-${day}`;
}

/**
 * The help under "Read changes since" (a backfill of the chosen tables). #1078: the date starts at
 * midnight in the reader's display zone, which the start sends as `tables.sinceTimeZone`, and the
 * zone is named: "from midnight in India (IST)".
 */
export function sinceHelp(zone: string): string {
  return `Reads rows changed on or after this date, from midnight in ${zoneLabel(zone)}. A table's own last import time never moves back.`;
}
